import axios from 'axios';
import { storage } from '@byscott-io/corebyscott';

const client = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

client.interceptors.request.use((config) => {
  const token = storage.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const accountId = storage.getContainerId();
  if (accountId) {
    config.headers['X-Account-Id'] = accountId;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      storage.clear();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const dashboard = {
  get: () => client.get('/dashboard'),
};

export const families = {
  list: () => client.get('/families'),
  get: (id) => client.get(`/families/${id}`),
  create: (family) => client.post('/families', { family }),
  update: (id, family) => client.patch(`/families/${id}`, { family }),
  delete: (id) => client.delete(`/families/${id}`),
};

export const students = {
  list: () => client.get('/students'),
  listForFamily: (familyId) => client.get(`/families/${familyId}/students`),
  get: (id) => client.get(`/students/${id}`),
  create: (familyId, student) => client.post(`/families/${familyId}/students`, { student }),
  update: (id, student) => client.patch(`/students/${id}`, { student }),
  delete: (id) => client.delete(`/students/${id}`),
  progress: (id) => client.get(`/students/${id}/progress`),
};

export const familyUsers = {
  list: (familyId) => client.get(`/families/${familyId}/family_users`),
  create: (familyId, email, familyUser) =>
    client.post(`/families/${familyId}/family_users`, { email, family_user: familyUser }),
  update: (familyId, id, familyUser) =>
    client.patch(`/families/${familyId}/family_users/${id}`, { family_user: familyUser }),
  delete: (familyId, id) => client.delete(`/families/${familyId}/family_users/${id}`),
};

export const memberships = {
  list: () => client.get('/memberships'),
  invite: (email, role, firstName, lastName) =>
    client.post('/memberships', { email, role, first_name: firstName, last_name: lastName }),
  update: (id, role) => client.patch(`/memberships/${id}`, { role }),
  remove: (id) => client.delete(`/memberships/${id}`),
  pending: () => client.get('/memberships/pending'),
  accept: (id) => client.post(`/memberships/${id}/accept`),
  decline: (id) => client.post(`/memberships/${id}/decline`),
  resendInvitation: (id) => client.post(`/memberships/${id}/resend_invitation`),
};

export const invitations = {
  getByToken: (token) => client.get(`/invitations/${token}`),
  acceptByToken: (token, data) => client.post(`/invitations/${token}/accept`, data),
};

export const accounts = {
  list: () => client.get('/containers'),
  create: (account) => client.post('/containers', { container: account }),
};

export const assessmentTemplates = {
  list: (studentId = null) => {
    const params = studentId ? { student_id: studentId } : {};
    return client.get('/assessment_templates', { params });
  },
  get: (id) => client.get(`/assessment_templates/${id}`),
  print: (id) => client.get(`/assessment_templates/${id}/print`, { responseType: 'blob' }),
};

export const assessmentSessions = {
  listForStudent: (studentId) => client.get(`/students/${studentId}/assessment_sessions`),
  create: (studentId, templateId, assessmentDate = null) =>
    client.post(`/students/${studentId}/assessment_sessions`, {
      assessment_template_id: templateId,
      assessment_date: assessmentDate,
    }),
  get: (id) => client.get(`/assessment_sessions/${id}`),
  update: (id, data) => client.patch(`/assessment_sessions/${id}`, data),
  submit: (id) => client.post(`/assessment_sessions/${id}/submit`),
  results: (id) => client.get(`/assessment_sessions/${id}/results`),
  // Teacher review endpoints
  pendingReview: () => client.get('/assessment_sessions/pending_review'),
  getReview: (id) => client.get(`/assessment_sessions/${id}/review`),
  scoreResponse: (sessionId, responseId, score, notes = null) =>
    client.patch(`/assessment_sessions/${sessionId}/review/${responseId}`, { score, notes }),
  finalizeReview: (id) => client.post(`/assessment_sessions/${id}/finalize_review`),
  // Export endpoints - returns blob for download
  exportPdf: (id) => client.get(`/assessment_sessions/${id}/export_pdf`, { responseType: 'blob' }),
  exportCsv: (id) => client.get(`/assessment_sessions/${id}/export_csv`, { responseType: 'blob' }),
};

export const groups = {
  list: () => client.get('/groups'),
  get: (id) => client.get(`/groups/${id}`),
  create: (group) => client.post('/groups', { group }),
  update: (id, group) => client.patch(`/groups/${id}`, { group }),
  delete: (id) => client.delete(`/groups/${id}`),
  analytics: (id) => client.get(`/groups/${id}/analytics`),
};

export const groupUsers = {
  list: (groupId) => client.get(`/groups/${groupId}/group_users`),
  create: (groupId, email, groupUser) =>
    client.post(`/groups/${groupId}/group_users`, { email, group_user: groupUser }),
  update: (groupId, id, groupUser) =>
    client.patch(`/groups/${groupId}/group_users/${id}`, { group_user: groupUser }),
  delete: (groupId, id) => client.delete(`/groups/${groupId}/group_users/${id}`),
};

export const groupStudents = {
  list: (groupId) => client.get(`/groups/${groupId}/students`),
  available: (groupId) => client.get(`/groups/${groupId}/students/available`),
  add: (groupId, studentId) => client.post(`/groups/${groupId}/students`, { student_id: studentId }),
  remove: (groupId, studentId) => client.delete(`/groups/${groupId}/students/${studentId}`),
};

export default client;
